// src/mocks/handlers/index.js
// Master URL router — maps every API endpoint to a mock handler.
// Order matters: first matching rule wins.

import * as analytics from './analytics.handlers';
import * as analyticsV2 from './analyticsV2.handlers';
import * as afipQueue from './afipQueue.handlers';
import * as brands from './brands.handlers';
import * as budgets from './budgets.handlers';
import * as clients from './clients.handlers';
import * as creditNotes from './creditNotes.handlers';
import * as cuentas from './cuentas.handlers';
import * as invoices from './invoices.handlers';
import * as operations from './operations.handlers';
import * as orders from './orders.handlers';
import * as packProducts from './packProducts.handlers';
import * as reports from './reports.handlers';
import * as runningAccount from './runningAccount.handlers';
import * as search from './search.handlers';
import * as signage from './signage.handlers';
import * as suppliers from './suppliers.handlers';
import * as auth from './auth.handlers';

// Each entry: { match(axiosConfig) → bool, handler(axiosConfig) → Promise<data> }
const ROUTES = [
    // ── Auth ─────────────────────────────────────────────────────────────────
    { match: (c) => /\/auth\/login/.test(c.url),   handler: auth.login },
    { match: (c) => /\/auth\/refresh/.test(c.url), handler: auth.refresh },

    // ── Cart (CartProvider fetches these on mount — must be mocked to prevent 401 loop) ─
    { match: (c) => /\/getvolumebasedprices/.test(c.url), handler: async () => ({ RangeValue1: 20, MarkValue1: 100, RangeValue2: 100, MarkValue2: 65 }) },
    { match: (c) => /\/postidcart/.test(c.url),           handler: async () => ({ dataresponse: [] }) },

    // ── Analytics V2 (must precede /analytics to avoid early match) ──────────
    { match: (c) => /\/analytics\/v2\/daily-sales-progress/.test(c.url), handler: analyticsV2.getDailySalesProgress },
    { match: (c) => /\/analytics\/v2\/sales-summary/.test(c.url),        handler: analyticsV2.getSalesSummary },
    { match: (c) => /\/analytics\/v2\/sales-by-category/.test(c.url),    handler: analyticsV2.getSalesByCategory },
    { match: (c) => /\/analytics\/v2\/payment-methods/.test(c.url),      handler: analyticsV2.getPaymentMethods },
    { match: (c) => /\/analytics\/v2\/profit-analysis/.test(c.url),      handler: analyticsV2.getProfitAnalysis },

    // ── Analytics V1 ─────────────────────────────────────────────────────────
    { match: (c) => /\/analytics\/dashboard/.test(c.url),          handler: analytics.getDashboard },
    { match: (c) => /\/analytics\/daily-invoice-stats/.test(c.url), handler: analytics.getDailyInvoiceStats },
    { match: (c) => /\/analytics\/daily-sales-stats/.test(c.url),   handler: analytics.getDailySalesStats },
    { match: (c) => /\/analytics\/average-invoice-value/.test(c.url), handler: analytics.getAverageInvoiceValue },
    { match: (c) => /\/analytics\/best-sellers/.test(c.url),        handler: analytics.getBestSellers },
    { match: (c) => /\/analytics\/top-products/.test(c.url),        handler: analytics.getTopProducts },
    { match: (c) => /\/analytics\/sales-by-category/.test(c.url),   handler: analytics.getSalesByCategory },
    { match: (c) => /\/analytics\/invoice-dates/.test(c.url),       handler: analytics.getInvoiceDates },
    { match: (c) => /\/analytics\/sales-trend/.test(c.url),         handler: analytics.getSalesTrend },
    { match: (c) => /\/analytics\/category-trend/.test(c.url),      handler: analytics.getCategoryTrend },
    { match: (c) => /\/analytics\/payment-methods/.test(c.url),     handler: analytics.getPaymentMethods },

    // ── AFIP Queue ────────────────────────────────────────────────────────────
    { match: (c) => /\/afip\/health/.test(c.url),                 handler: afipQueue.getHealth      },
    { match: (c) => /\/afip\/queue\/stats/.test(c.url),          handler: afipQueue.getStats       },
    { match: (c) => /\/afip\/queue\/my-invoices/.test(c.url),    handler: afipQueue.getMyInvoices  },
    { match: (c) => /\/afip\/queue\/sync-processing/.test(c.url), handler: afipQueue.syncProcessing },
    { match: (c) => /\/afip\/queue/.test(c.url),                  handler: afipQueue.getQueue       },

    // ── Brands / Categories ───────────────────────────────────────────────────
    { match: (c) => /\/getbrandquantities/.test(c.url), handler: brands.getBrandQuantities },
    { match: (c) => /\/getbrandvariables/.test(c.url),  handler: brands.getBrandVariables },
    { match: (c) => /\/getallbrands/.test(c.url),        handler: brands.getAllBrands },
    { match: (c) => /\/getcategorynames/.test(c.url),    handler: brands.getCategoryNames },

    // ── Pack Products (before /products to avoid conflicts) ──────────────────
    { match: (c) => /\/pack-products\/unified-stock\/stats/.test(c.url), handler: packProducts.getUnifiedStockStats },
    { match: (c) => /\/pack-products\/unified-stock/.test(c.url),        handler: packProducts.getUnifiedStock },
    { match: (c) => /\/pack-products\/stats/.test(c.url),                handler: packProducts.getStats },
    { match: (c) => /\/pack-products\/brands/.test(c.url),               handler: packProducts.getBrands },
    { match: (c) => /\/pack-products\/bulk-verify/.test(c.url),          handler: packProducts.bulkVerify },
    { match: (c) => /\/pack-products\/refresh/.test(c.url),              handler: packProducts.refresh },
    { match: (c) => /\/pack-products/.test(c.url),                       handler: packProducts.handle },

    // ── Budget Quotes ─────────────────────────────────────────────────────────
    { match: (c) => /\/budget-quotes/.test(c.url), handler: budgets.handle },

    // ── Clients ───────────────────────────────────────────────────────────────
    { match: (c) => /\/clients\/validate-cuit-afip/.test(c.url), handler: clients.validateCuit },
    { match: (c) => /\/clients\/search-name\//.test(c.url),       handler: clients.searchByName },
    { match: (c) => /\/clients\/search-cuit\//.test(c.url),       handler: clients.searchByCuit },
    { match: (c) => /\/clients\/save/.test(c.url),                handler: clients.save },
    { match: (c) => /\/clients/.test(c.url),                      handler: clients.handle },

    // ── Credit Notes ──────────────────────────────────────────────────────────
    { match: (c) => /\/credit-notes\/creditable-items\//.test(c.url), handler: creditNotes.getCreditableItems },
    { match: (c) => /\/credit-notes\/can-credit\//.test(c.url),        handler: creditNotes.canCredit },
    { match: (c) => /\/credit-notes\/by-invoice\//.test(c.url),        handler: creditNotes.getByInvoice },
    { match: (c) => /\/credit-notes\/retry\//.test(c.url),             handler: creditNotes.retry },
    { match: (c) => /\/credit-notes\/[^/]+\/pdf/.test(c.url),          handler: creditNotes.getPdf },
    { match: (c) => /\/credit-notes/.test(c.url),                      handler: creditNotes.handle },

    // ── Cuentas Corrientes ────────────────────────────────────────────────────
    { match: (c) => /\/cuentas-corrientes/.test(c.url), handler: cuentas.handle },

    // ── Invoices ──────────────────────────────────────────────────────────────
    { match: (c) => /\/invoices\/limit-check/.test(c.url),       handler: invoices.limitCheck },
    { match: (c) => /\/invoices\/retry\//.test(c.url),           handler: invoices.retry },
    { match: (c) => /\/invoices\/[^/]+\/audit/.test(c.url),      handler: invoices.getAudit },
    { match: (c) => /\/invoices\/[^/]+\/pdf/.test(c.url),        handler: invoices.getPdf },
    { match: (c) => /\/invoices\/[^/]+\/qr/.test(c.url),         handler: invoices.getQr },
    { match: (c) => /\/invoices/.test(c.url),                    handler: invoices.handle },

    // ── Operations ────────────────────────────────────────────────────────────
    { match: (c) => /\/operations\/start/.test(c.url),                    handler: operations.start },
    { match: (c) => /\/operations\/current\/movements/.test(c.url),       handler: operations.getMovements },
    { match: (c) => /\/operations\/current/.test(c.url),                   handler: operations.getCurrent },
    { match: (c) => /\/operations\/withdrawal/.test(c.url),      handler: operations.withdrawal },
    { match: (c) => /\/operations\/deposit/.test(c.url),         handler: operations.deposit },
    { match: (c) => /\/operations\/movement/.test(c.url),        handler: operations.movement },
    { match: (c) => /\/operations\/closing-summary/.test(c.url), handler: operations.getClosingSummary },
    { match: (c) => /\/operations\/close/.test(c.url),           handler: operations.close },
    { match: (c) => /\/operations\/report\//.test(c.url),        handler: operations.getReport },
    { match: (c) => /\/operations/.test(c.url),                  handler: operations.handle },

    // ── Orders / Pedidos / Products ───────────────────────────────────────────
    { match: (c) => /\/orders\/supplier\/[^/]+\/search-products/.test(c.url), handler: orders.searchProductsBySupplier },
    { match: (c) => /\/products\/check-duplicates/.test(c.url),               handler: orders.checkDuplicates },
    { match: (c) => /\/orders\/[^/]+\/attachments/.test(c.url),               handler: orders.getAttachments },
    { match: (c) => /\/orders\/[^/]+\/items/.test(c.url),                     handler: orders.getItems },
    { match: (c) => /\/orders\/[^/]+\/status/.test(c.url),                    handler: orders.updateStatus },
    { match: (c) => /\/orders\/[^/]+\/cancel/.test(c.url),                    handler: orders.cancel },
    { match: (c) => /\/orders\/create/.test(c.url),                           handler: orders.create },
    { match: (c) => /\/orders\/supplier\//.test(c.url),                       handler: orders.getBySupplier },
    { match: (c) => /\/orders/.test(c.url),                                   handler: orders.handle },

    // ── Running Account ───────────────────────────────────────────────────────
    { match: (c) => /\/runningaccount/.test(c.url), handler: runningAccount.handle },

    // ── Reports ───────────────────────────────────────────────────────────────
    { match: (c) => /\/reports\/monthly/.test(c.url), handler: reports.getMonthly },

    // ── Signage ───────────────────────────────────────────────────────────────
    { match: (c) => /\/signage\/campaigns\/deactivate/.test(c.url),        handler: signage.deactivateCampaigns },
    { match: (c) => /\/signage\/campaigns\/[^/]+\/activate/.test(c.url),   handler: signage.activateCampaign },
    { match: (c) => /\/signage\/campaigns/.test(c.url),                    handler: signage.campaigns },
    { match: (c) => /\/signage\/slides\//.test(c.url),                     handler: signage.slides },
    { match: (c) => /\/signage\/active/.test(c.url),                       handler: signage.getActive },

    // ── Suppliers ─────────────────────────────────────────────────────────────
    { match: (c) => /\/suppliers\/search-cuit\//.test(c.url), handler: suppliers.searchByCuit },
    { match: (c) => /\/suppliers\/save/.test(c.url),          handler: suppliers.save },
    { match: (c) => /\/suppliers/.test(c.url),                handler: suppliers.handle },

    // ── Product Search ────────────────────────────────────────────────────────
    { match: (c) => /\/api\/v3\/search/.test(c.url), handler: search.searchProducts },
    { match: (c) => /\/setisfavorite/.test(c.url),   handler: search.setFavorite },
];

/**
 * Find the first matching handler for an axios request config.
 * Returns undefined if no match → real network request proceeds.
 */
export const resolveHandler = (config) => {
    const entry = ROUTES.find((r) => {
        try {
            return r.match(config);
        } catch {
            return false;
        }
    });
    return entry ? entry.handler : undefined;
};
